import React from "react";
import { useTranslation } from "react-i18next";
import {
  Star,
  Heart,
  Share2,
  ExternalLink,
  Minus,
  Plus,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCommercePaths } from "@/modules";
import { useProductDetail } from "../hooks/use-product-detail";
import { OtherSellersSection } from "./OtherSellersSection";
import { Link } from "react-router-dom";

interface ProductInfoSectionProps {
  variant?: "modal" | "page";
}

export const ProductInfoSection: React.FC<ProductInfoSectionProps> = ({
  variant = "page",
}) => {
  const { t } = useTranslation();
  const paths = useCommercePaths();
  const {
    product,
    productId,
    brand,
    seller,
    quantity,
    isAuthenticated,
    isLoading,
    onIncrementQuantity,
    onDecrementQuantity,
    onAddToCart,
    onBuyNow,
    onToggleFavorite,
    isFavorite,
    isFavoritePending,
    onShare,
    onViewFullPage,
    onVariantSelect, // Varyant seçimi için
  } = useProductDetail();

  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      {/* Brand/Seller Badge + Actions */}
      <div className="flex items-center justify-between gap-2">
        {!isLoading && (brand || seller) && (
          <div className="bg-bg-secondary dark:bg-bg-tertiary border border-border-secondary dark:border-border-primary rounded-md sm:rounded-[6px] px-1.5 sm:px-2 py-1 sm:py-1.5 flex items-center gap-1 sm:gap-1.5 max-w-[60%] sm:max-w-fit min-w-0">
            {seller?.logoUrl && (
              <img
                src={seller.logoUrl}
                alt={seller.name || brand?.name}
                className="h-3.5 sm:h-4 w-auto max-w-[60px] sm:max-w-[80px] object-contain flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}
            <span className="text-[11px] sm:text-[12px] font-medium text-text-secondary dark:text-text-secondary whitespace-nowrap truncate">
              {seller?.name || brand?.name}
            </span>
            {product.url && (
              <a
                href={product.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-text-primary dark:text-text-secondary dark:hover:text-text-primary flex-shrink-0"
              >
                <ExternalLink className="size-2.5 sm:size-3" />
              </a>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <button
            onClick={onToggleFavorite}
            disabled={isFavoritePending}
            aria-pressed={isFavorite}
            className="bg-white dark:bg-bg-secondary border border-border-primary dark:border-border-primary rounded-full p-1.5 sm:p-2 hover:bg-bg-secondary dark:hover:bg-bg-tertiary transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={
              isFavorite
                ? t("favorites.remove")
                : t("productDetail.addToFavorites")
            }
          >
            <Heart
              className={cn(
                "size-3 sm:size-3.5 transition-colors",
                isFavorite
                  ? "fill-current text-module-accent"
                  : "text-text-secondary dark:text-text-secondary",
              )}
            />
          </button>
          <button
            onClick={onShare}
            className="bg-white dark:bg-bg-secondary border border-border-primary dark:border-border-primary rounded-full p-1.5 sm:p-2 hover:bg-bg-secondary dark:hover:bg-bg-tertiary transition-colors"
            aria-label={t("productDetail.share")}
          >
            <Share2 className="size-3 sm:size-3.5 text-text-secondary dark:text-text-secondary" />
          </button>
        </div>
      </div>

      {/* Title + Rating */}
      <div className="flex flex-col gap-1">
        <h2 className="text-base sm:text-lg lg:text-[20px] font-medium leading-tight sm:leading-[26px] lg:leading-[30px] text-text-primary dark:text-text-primary">
          {product.brand && (
            <span className="font-medium">{product.brand} </span>
          )}
          <span className="font-normal">{product.title}</span>
        </h2>
        {/* Rating - Sadece gerçek data varsa göster */}
        {!isLoading && product.rating && product.reviewCount && (
          <div className="flex items-center gap-1">
            <Star className="size-3 sm:size-3.5 fill-text-brand text-text-brand" />
            <span className="text-[11px] sm:text-[12px] font-medium text-text-primary dark:text-text-primary">
              {product.rating}{" "}
              <span className="font-normal text-text-tertiary dark:text-text-tertiary">
                ({(product.reviewCount as number).toLocaleString()})
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Price - TL sağda, diğer para birimleri solda */}
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
        <span className="text-lg sm:text-xl lg:text-[24px] font-medium leading-tight sm:leading-[28px] lg:leading-[32px] text-text-primary dark:text-text-primary">
          {product.formattedPrice?.formatted ||
            `${product.currency}${product.price || product.oldPrice}`}
        </span>
        {product.oldPrice &&
          product.price &&
          product.oldPrice > product.price && (
            <>
              <span className="text-sm sm:text-base text-text-tertiary dark:text-text-tertiary line-through">
                {product.formattedOldPrice?.formatted ||
                  `${product.currency}${product.oldPrice}`}
              </span>
              {product.discountPct && product.discountPct > 0 && (
                <div className="bg-bg-brand dark:bg-bg-brand border border-border-brand dark:border-border-brand rounded-md sm:rounded-[6px] px-1 sm:px-[6px] py-0.5 sm:py-[2px] flex items-center gap-0.5 sm:gap-1">
                  <span className="text-[10px] sm:text-[12px] font-medium text-text-brand dark:text-text-brand">
                    %{product.discountPct} {t("shopping.off")}
                  </span>
                </div>
              )}
            </>
          )}
      </div>

      {/* Slicer Groups - Cross-product navigation (Color, Material, etc.) */}
      {!isLoading &&
        product.slicerGroups?.map((group: any) => (
          <div key={group.attributeId} className="flex flex-col gap-1">
            <p className="text-[11px] sm:text-[12px] font-bold text-text-tertiary dark:text-text-tertiary">
              {group.attributeName}:
            </p>
            <div className="flex gap-1.5 sm:gap-2 flex-wrap">
              {group.values.map((item: any) => (
                <button
                  key={item.valueId}
                  onClick={() => onVariantSelect(item.productId)}
                  disabled={!item.hasAnyStock}
                  className={cn(
                    "border rounded-md sm:rounded-[6px] overflow-hidden transition-colors",
                    item.imageUrl
                      ? "w-12 h-12 sm:w-14 sm:h-14"
                      : "px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium leading-tight sm:leading-[20px] min-w-[45px] sm:min-w-[50px] flex items-center justify-center",
                    !item.hasAnyStock
                      ? "border-border-secondary dark:border-border-primary opacity-50 cursor-not-allowed"
                      : item.isSelected
                      ? "border-text-primary dark:border-white ring-1 ring-text-primary dark:ring-white"
                      : "border-border-secondary dark:border-border-primary hover:border-text-primary dark:hover:border-text-primary"
                  )}
                >
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.valueName}
                      className={cn(
                        "w-full h-full object-cover",
                        !item.hasAnyStock && "grayscale"
                      )}
                    />
                  ) : (
                    item.valueName
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}

      {/* First Variant Group - Dinamik (Renk, Hafıza, GB vb.) */}
      {!isLoading &&
        (() => {
          const validVariants = product.firstVariantGroup?.filter(
            (v: any) => v.attributeName && v.attributeValue
          );
          if (!validVariants || validVariants.length === 0) return null;

          return (
            <div className="flex flex-col gap-1">
              <p className="text-[11px] sm:text-[12px] font-bold text-text-tertiary dark:text-text-tertiary">
                {validVariants[0].attributeName}:
              </p>
              <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                {validVariants.map((variant: any) => (
                  <button
                    key={variant.productId}
                    onClick={() => onVariantSelect(variant.productId)}
                    disabled={!variant.hasAnyStock || !variant.isOpen}
                    className={cn(
                      "border rounded-md sm:rounded-[6px] px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium leading-tight sm:leading-[20px] min-w-[45px] sm:min-w-[50px] flex items-center justify-center transition-colors",
                      variant.isSelected
                        ? "bg-text-primary dark:bg-white border-text-primary dark:border-white text-white dark:text-gray-900"
                        : variant.hasAnyStock && variant.isOpen
                        ? "bg-bg-secondary dark:bg-bg-tertiary border-border-secondary dark:border-border-primary text-text-secondary dark:text-text-secondary hover:border-text-primary dark:hover:border-text-primary"
                        : "bg-bg-secondary dark:bg-bg-tertiary border-border-secondary dark:border-border-primary text-text-tertiary dark:text-text-tertiary opacity-50 cursor-not-allowed"
                    )}
                  >
                    {variant.attributeValue}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

      {/* Second Variant Group - Dinamik (Beden, Renk vb.) */}
      {!isLoading &&
        (() => {
          const validVariants = product.secondVariantGroup?.filter(
            (v: any) => v.attributeName && v.attributeValue
          );
          if (!validVariants || validVariants.length === 0) return null;

          return (
            <div className="flex flex-col gap-1">
              <p className="text-[11px] sm:text-[12px] font-bold text-text-tertiary dark:text-text-tertiary">
                {validVariants[0].attributeName}:
              </p>
              <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                {validVariants.map((variant: any) => (
                  <button
                    key={variant.productId}
                    onClick={() => onVariantSelect(variant.productId)}
                    disabled={!variant.hasAnyStock || !variant.isOpen}
                    className={cn(
                      "border rounded-md sm:rounded-[6px] px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium leading-tight sm:leading-[20px] min-w-[45px] sm:min-w-[50px] flex items-center justify-center transition-colors",
                      variant.isSelected
                        ? "bg-text-primary dark:bg-white border-text-primary dark:border-white text-white dark:text-gray-900"
                        : variant.hasAnyStock && variant.isOpen
                        ? "bg-bg-secondary dark:bg-bg-tertiary border-border-secondary dark:border-border-primary text-text-secondary dark:text-text-secondary hover:border-text-primary dark:hover:border-text-primary"
                        : "bg-bg-secondary dark:bg-bg-tertiary border-border-secondary dark:border-border-primary text-text-tertiary dark:text-text-tertiary opacity-50 cursor-not-allowed"
                    )}
                  >
                    {variant.attributeValue}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

      {/* Stock Status - Stok durumu ve low stock uyarısı (sadece data yüklendiyse göster) */}
      {!isLoading && (
        <>
          {!product.inStock ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg sm:rounded-[8px] px-2.5 sm:px-3 py-1.5 sm:py-2">
              <p className="text-[11px] sm:text-[12px] font-medium text-red-600 dark:text-red-400">
                {t("productDetail.outOfStock")}
              </p>
            </div>
          ) : (
            product.lowStock && (
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg sm:rounded-[8px] px-2.5 sm:px-3 py-1.5 sm:py-2">
                <p className="text-[11px] sm:text-[12px] font-medium text-orange-600 dark:text-orange-400">
                  {t("productDetail.lowStock")}
                  {product.stockCount &&
                    ` (${product.stockCount} ${t("productDetail.itemsLeft")})`}
                </p>
              </div>
            )
          )}
        </>
      )}

      {/* Other Sellers */}
      {!isLoading && <OtherSellersSection />}

      <div className="bg-border-secondary dark:bg-border-secondary h-px w-full" />

      {/* Quantity + Buy Button (Page view has quantity) */}
      <div className="flex gap-2 sm:gap-4">
        <div className="bg-white dark:bg-bg-secondary border border-border-secondary dark:border-border-primary rounded-lg sm:rounded-[8px] shrink-0">
          <div className="flex items-center gap-4 sm:gap-6 px-2 sm:px-3 py-2 sm:py-2.5">
            <button
              onClick={onDecrementQuantity}
              disabled={quantity <= 1}
              className="text-text-tertiary dark:text-text-tertiary hover:text-text-primary dark:hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label={t("productDetail.decreaseQuantity")}
            >
              <Minus className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
            </button>
            <span className="text-xs sm:text-sm font-semibold text-text-primary dark:text-text-primary min-w-[20px] sm:min-w-[24px] text-center">
              {quantity}
            </span>
            <button
              onClick={onIncrementQuantity}
              className="text-text-primary dark:text-text-primary hover:text-text-brand dark:hover:text-text-brand transition-colors"
              aria-label={t("productDetail.increaseQuantity")}
            >
              <Plus className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
            </button>
          </div>
        </div>

        {isAuthenticated ? (
          <div className="flex gap-2 sm:gap-4 flex-1">
            <Button
              variant="darkPrimary"
              onClick={onAddToCart}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg bg-gray-900 dark:bg-gray-800 text-white text-xs sm:text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("productDetail.addToCartButton")}
            </Button>
            {variant === "page" && (
              <Button
                variant="darkPrimary"
                onClick={onBuyNow}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg bg-gray-900 dark:bg-gray-800 text-white text-xs sm:text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("productDetail.buyNow")}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex-1 space-y-1.5 sm:space-y-2">
            <Button
              asChild
              variant="darkPrimary"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg bg-gray-900 dark:bg-gray-800 text-white text-xs sm:text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors shadow-sm"
            >
              <Link to="/login">{t("productDetail.loginToBuy")}</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-xs sm:text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Link to="/register">{t("productDetail.createAccount")}</Link>
            </Button>
          </div>
        )}
      </div>

      {variant === "page" && (
        <Button
          asChild
          variant="outline"
          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-border-primary dark:border-border-primary bg-bg-primary dark:bg-bg-secondary text-text-primary dark:text-white text-xs sm:text-sm font-medium hover:bg-bg-secondary dark:hover:bg-bg-tertiary transition-colors"
        >
          <Link to={`${paths.wardrobe()}?tryOnProductId=${productId}`}>
            {t("wardrobe.tryOnYourself")}
          </Link>
        </Button>
      )}

      {variant === "modal" && (
        <>
          <div className="bg-border-secondary dark:bg-border-secondary h-px w-full" />

          {/* Actions - Modal specific */}
          <div className="flex gap-2 sm:gap-4">
            <Link
              to={paths.product(productId ?? "")}
              onClick={onViewFullPage}
              className="px-3 sm:px-3.5 py-2 sm:py-2.5 rounded-lg bg-white dark:bg-gray-900 flex items-center gap-1.5 sm:gap-2 text-text-secondary dark:text-text-secondary text-xs sm:text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("productDetail.moreDetails")}
              <ArrowUpRight className="size-3.5 sm:size-4" />
            </Link>
            <Button
              variant="darkPrimary"
              onClick={onBuyNow}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg bg-gray-900 dark:bg-gray-800 text-white text-xs sm:text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("productDetail.buyNow")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
